'use client'
import Image from 'next/image'

import { FC } from 'react'

import { AnimationMoveIconComponent } from '@/app/shared/components/animation/move-icon'
import { AnimationNumberComponent } from '@/app/shared/components/animation/number'
import { AnimationShowBlockComponent } from '@/app/shared/components/animation/show-block'
import SignUpLessonComponent from '@/app/shared/components/modals/sign-up-lesson/sign-up-lesson.component'
import { IId } from '@/app/shared/interfaces/common.interface'
import {
  IButton,
  IContent,
  IDescription,
  IImg,
  ILabel,
  ISimpleTitle,
  ISticker,
  ISubTitle,
  ITitle,
} from '@/app/shared/interfaces/content.interface'
import { clearData } from '@/app/shared/services/data-formatter'
import useModal from '@/app/shared/store/modals.store'
import { ButtonComponent } from '@/app/shared/ui/button'
import { ContainerComponent } from '@/app/shared/ui/container'
import { TitleComponent } from '@/app/shared/ui/title'

// interface
interface IAbout extends IId {
  data: IAboutQuery
}

export interface IAboutQuery {
  title: ITitle
  subTitle: ISubTitle
  sticker: ISticker[]
  services: ISimpleTitle & {
    items: IContent[]
  }
  image: IImg[]
  descriptionEditor: {
    descriptionEditor: string
  }
  button: IButton
}

interface IDetailItem {
  detailItem: (IDescription & ILabel)[]
}

const AboutComponent: FC<Readonly<IAbout>> = ({ data, id }) => {
  const statistics = (clearData(data.services.items) as IDetailItem).detailItem

  const open = useModal((state) => state.open)

  const handleOpenModal = () => {
    open({
      component: SignUpLessonComponent,
      props: {
        title: data.button.text,
      },
    })
  }

  return (
    <ContainerComponent
      id={id}
      className={
        'bg-content1-900 py-[60px] laptop:pb-[120px] laptop:pt-[120px] desktop:pb-[140px] desktop:pt-[140px] large:pb-[163px] large:pt-40'
      }
    >
      <div
        className={
          'grid grid-cols-1 gap-[60px] laptop:grid-cols-[1fr_450px] laptop:gap-[20px] desktop:grid-cols-[1fr_570px] large:grid-cols-[1fr_780px]'
        }
      >
        <div className={'flex flex-col'}>
          <TitleComponent className={'mb-[42px] text-content1-200'} animate>
            {data?.title?.title}
          </TitleComponent>
          <AnimationShowBlockComponent
            initial={{
              opacity: 0,
              x: -20,
            }}
            whileInView={{
              opacity: 1,
              x: 0,
            }}
          >
            <div
              className={'mb-[50px] text-[22px] !leading-normal desktop:text-[30px] large:text-4xl'}
              dangerouslySetInnerHTML={{ __html: data?.subTitle?.subTitle }}
            ></div>
          </AnimationShowBlockComponent>
          <AnimationShowBlockComponent
            initial={{
              opacity: 0,
              x: 20,
            }}
            whileInView={{
              opacity: 1,
              x: 0,
            }}
          >
            <div
              className={
                'mb-10 text-[16px] leading-relaxed laptop:text-[18px] desktop:text-[22px] large:mb-2.5 large:text-[22px]'
              }
              dangerouslySetInnerHTML={{ __html: data?.descriptionEditor?.descriptionEditor }}
            ></div>
          </AnimationShowBlockComponent>
          <div className={'mt-auto grid gap-[34px] mobile:grid-cols-2 laptop:gap-4'}>
            {statistics?.map((el, index) => (
              <AnimationShowBlockComponent
                key={index}
                className={
                  'grid h-auto min-h-0 w-full flex-col justify-between gap-y-[34px] rounded-large border-2 border-white p-5 font-extrabold text-white laptop:flex desktop:min-h-[230px] large:min-h-[260px]'
                }
                initial={{
                  opacity: 0,
                  y: 20,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                transition={{
                  delay: (index + 1) * 0.3,
                }}
              >
                <span className={'mb-2 text-[16px] desktop:text-[16px] large:text-2xl'}>
                  {el?.label}
                </span>
                <AnimationNumberComponent
                  value={+el?.description}
                  className={
                    'mt-auto text-center text-[40px] leading-[0.9] mobile:text-[60px] desktop:text-[80px] large:text-[110px]'
                  }
                />
              </AnimationShowBlockComponent>
            ))}
          </div>
        </div>
        <div className={'flex flex-col overflow-hidden laptop:overflow-visible'}>
          <div className={'relative mb-14 grid gap-5 laptop:gap-[52px]'}>
            <AnimationShowBlockComponent
              className={
                'relative ml-auto h-[190px] w-[294px] laptop:h-[130px] laptop:w-[209px] large:h-[238px] large:w-[371px]'
              }
              initial={{
                opacity: 0,
                x: 20,
              }}
              whileInView={{
                opacity: 1,
                x: 0,
              }}
            >
              <Image
                src={data?.image[0]?.image}
                alt={'about first'}
                fill
                className={'object-contain'}
                sizes={'(max-width: 1280px):294px, (max-width: 1920px):209px, 371px'}
              />
            </AnimationShowBlockComponent>
            <AnimationShowBlockComponent
              className={
                'relative h-[220px] w-full extraMobile:w-[320px] laptop:h-[219px] laptop:w-[302px] large:ml-10 large:h-[334px] large:w-[486px]'
              }
              initial={{
                opacity: 0,
                x: -20,
              }}
              whileInView={{
                opacity: 1,
                x: 0,
              }}
            >
              <Image
                src={data.image[2].image}
                alt={'about first'}
                fill
                className={'object-contain'}
                sizes={
                  '(max-width: 520px):100vw, (max-width: 1280px):320px, (max-width: 1920px):302px, 486px'
                }
              />
            </AnimationShowBlockComponent>
            <AnimationShowBlockComponent
              className={
                'relative ml-auto h-[194px] w-full extraMobile:w-[256px] laptop:h-[195px] laptop:w-[256px] large:h-[318px] large:w-[418px]'
              }
              initial={{
                opacity: 0,
                x: 20,
              }}
              whileInView={{
                opacity: 1,
                x: 0,
              }}
            >
              <Image
                src={data?.image[1]?.image}
                alt={'about first'}
                fill
                className={'object-contain'}
                sizes={'(max-width: 1280px):100vw, (max-width: 1920px):256px, 418px'}
              />
            </AnimationShowBlockComponent>
            {data?.sticker[0]?.image && (
              <AnimationMoveIconComponent
                className={
                  'absolute right-0 top-1/2 h-[46px] w-full -rotate-12 extraMobile:w-[87px] large:h-[73px] large:w-[137px]'
                }
                rotate={-12}
                valueMoving={55}
                initial={{
                  opacity: 0,
                }}
                whileInView={{
                  opacity: 1,
                }}
              >
                <Image
                  src={data?.sticker[0]?.image}
                  alt={'sticker'}
                  fill
                  className={'object-contain'}
                  sizes={'(max-width: 520px):100vw, (max-width: 1920px):87px, 137px'}
                />
              </AnimationMoveIconComponent>
            )}
            {data?.sticker[1]?.image && (
              <AnimationMoveIconComponent
                className={
                  'absolute left-[15%] top-[45px] h-[54px] w-full rotate-12 extraMobile:w-[55px] laptop:left-[55%] desktop:h-[74px] desktop:w-[72px] large:left-[48%] large:top-[78px]'
                }
                rotate={12}
                valueMoving={20}
                initial={{
                  opacity: 0,
                }}
                whileInView={{
                  opacity: 1,
                }}
              >
                <Image
                  src={data?.sticker[1]?.image}
                  alt={'sticker'}
                  fill
                  className={'object-contain'}
                  sizes={'(max-width: 520px):100pvw, (max-width: 1600px):55px, 72px'}
                />
              </AnimationMoveIconComponent>
            )}
          </div>
          <AnimationShowBlockComponent
            initial={{
              opacity: 0,
              y: 20,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
          >
            <ButtonComponent
              color={'primary'}
              size={'lg'}
              className={'group mt-auto bg-content1-200 text-foreground'}
              icon={'ml-5 h-8 w-8 text-content1-100 group-hover:!text-content1-100'}
              onClick={handleOpenModal}
            >
              {data?.button?.text}
            </ButtonComponent>
          </AnimationShowBlockComponent>
        </div>
      </div>
    </ContainerComponent>
  )
}

export default AboutComponent
